import { Injectable } from '@angular/core';

import { IBank, IBPProfile, ICompanyGroup, IContact, ICountryExtended, ICurrency, IObject, IValueObject } from '../../../../type_definitions/datatypes';
import { BPProfilesDataService, IBPWizardProgress } from '../bp-profiles-data.service';
import { BPProfilesTCDataService } from '../advanced/tc/bp-profiles-tc-data.service';
import { BPProfilesAttributesService, IProfileAttribute, IProfileAttributeRequest, IProfileAttributeResponse, ProfileAttributeAgreeType, ProfileAttributeCode, ProfileAttributeValueType } from '../profile-attributes/bp-profiles-attributes.service';
import { Observable, Subject } from 'rxjs';
import { BPProfilesService } from '../bp-profiles.service';
import { util } from '../../../shared/util';
import * as _ from 'underscore';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { GaWizard } from '../../../shared/wizard/ga-wizard';
import { WIZARD_BP_WIZARD } from '../../../shared/app-wizards';
import { TranslationService } from '../../../shared/translation/translation.service';
import { GaFormlyService } from '../../../shared/formly/ga-formly.service';
import { IOEMRelationExtended } from '../advanced/oem/bp-profiles-oem-data.service';

export enum BPWizardStep {
  GENERAL = 'GENERAL',
  OEM = 'OEM',
  COMPANY_MARGIN = 'MARGIN',
  PAYMENT_TERMS = 'PAYMENTTERMS',
  BILLING_METHODS = 'BILLING',
  PAYMENT_SOURCE = 'PAYMENTSOURCE',
  BANK = 'BANK',
  FEES = 'FEES',
  AGREEMENT = 'AGREEMENT'
}

export enum BPWizardAction {
  SWITCH_TO_ADVANCED = 'switchToAdvanced',
  SAVE = 'save',
  SUBMIT = 'submit'
}

export interface IBPProfileAttributeData {
  defaultResellerFee?:number;
  resellerFee?:number;
  resellerFeeComment?:string;
  paymentTerms?:number;
  paymentTermsComment?:string;
  invoiceCurrencyEUR?:IValueObject;
  invoiceCurrencyUSD?:IValueObject;
  customerCCPayment?:IValueObject;
  ccCharge?:number;
  paypalCharge?:number;
  payACSLFees?:IValueObject;
  withholdingTaxes?:string;
}

export interface IBPProfileData extends IBPProfileAttributeData {
  id?:number;
  companyGroup?:ICompanyGroup;
  country?:ICountryExtended;
  contractCurrency?:ICurrency;
  taxNumber?:string;
  vatName?:string;
  businessRegistrationNr?:string;
  companyName?:string;
  contractAgreed?:boolean;
  orderFees?:string;
  memberPercentageOFFee?:number;
  contractAgreedBy?:IContact;
  contractAgreedOn?:number;
  localBank:IBank;
  usdBank:IBank;
  euroBank:IBank;
}

export const PAYMENT_TERMS_DEFAULT:number = 30; // 30 days

@Injectable()
export class BPWizardService {
  private _model:IBPProfileData;
  private _tc:IProfileAttributeResponse;
  private _progress:IBPWizardProgress;
  private _oemRelations:IOEMRelationExtended[];
  private _backupModel:IBPProfileData;

  private wizard:GaWizard;
  private onProgressChangeSubject:Subject<IBPWizardProgress> = new Subject<IBPWizardProgress>();
  private onSaveSubject:Subject<boolean> = new Subject<boolean>();
  private onUpdateWizardButtonStatusSubject:Subject<boolean> = new Subject<boolean>();
  private onSaveButtonEnabledChangeSubject:Subject<boolean> = new Subject<boolean>();

  public onProgressChange:Observable<IBPWizardProgress> = this.onProgressChangeSubject.asObservable();
  public onSave:Observable<boolean> = this.onSaveSubject.asObservable();
  public onUpdateWizardButtonStatus:Observable<boolean> = this.onUpdateWizardButtonStatusSubject.asObservable();
  public onSaveButtonEnabledChange:Observable<boolean> = this.onSaveButtonEnabledChangeSubject.asObservable();

  constructor(
    private bpProfilesService:BPProfilesService,
    private bpProfilesDataService:BPProfilesDataService,
    private bpProfilesTCDataService:BPProfilesTCDataService,
    private wizardService:GaWizardService,
    private translationService:TranslationService,
    private formlyService:GaFormlyService,
    private bpProfilesAttributesService:BPProfilesAttributesService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_BP_WIZARD);
  }

  public get tc():IProfileAttributeResponse {
    return this._tc;
  }

  public set tc(tc:IProfileAttributeResponse) {
    const codes:string[] = [
      ProfileAttributeCode.RESELLER_MARGIN,
      ProfileAttributeCode.STANDARD_PAYMENT_TERMS,
      ProfileAttributeCode.INVOICE_CURRENCY_EUR_FOREIGN,
      ProfileAttributeCode.INVOICE_CURRENCY_USD_FOREIGN,
      ProfileAttributeCode.CUSTOMER_CC_PAYMENT,
      ProfileAttributeCode.CC_MARGIN,
      ProfileAttributeCode.PAYPAL_MARGIN,
      ProfileAttributeCode.ACSL_FREE_TAX_LOCAL
    ];

    if (tc) {
      tc.profileAttributes = _.filter(tc.profileAttributes, (pa:IProfileAttribute) => {
        return _.contains(codes, pa.code);
      });

      // set attributeValue
      _.each(tc.profileAttributes, (profileAttribute:IProfileAttribute) => {
        if (!profileAttribute.attributeValue) {
          profileAttribute.attributeValue = <any>{};
        }
      });
    }

    this._tc = tc;
  }

  public get progress():IBPWizardProgress {
    return this._progress;
  }

  public set progress(progress:IBPWizardProgress) {
    this._progress = progress;
    this.onProgressChangeSubject.next(progress);
  }

  public get model():IBPProfileData {
    return this._model;
  }

  public set model(model:IBPProfileData) {
    this._model = model;
  }

  public get backupModel():IBPProfileData {
    return this._backupModel;
  }

  public set backupModel(backupModel:IBPProfileData) {
    this._backupModel = backupModel;
  }

  public get oemRelations():IOEMRelationExtended[] {
    return this._oemRelations;
  }

  public set oemRelations(oemRelations:IOEMRelationExtended[]) {
    this._oemRelations = oemRelations;
  }

  public emitSave(isSubmit:boolean = false):void {
    this.onSaveSubject.next(isSubmit);
  }

  public emitUpdateWizardButtonStatus(isBusy:boolean):void {
    this.onUpdateWizardButtonStatusSubject.next(isBusy);
  }

  public emitSaveButtonEnabled(isEnabled?:boolean):void {
    this.onSaveButtonEnabledChangeSubject.next(isEnabled);
  }

  public initBPWizardData(profileId:number):Promise<void> {
    if (profileId) {
      this.bpProfilesService.activeProfile = { id: profileId };

      return Promise.all([
        this.bpProfilesService.loadProfileDetails(),
        this.bpProfilesTCDataService.loadTC(profileId),
        this.bpProfilesDataService.getProfileProgress(profileId)
      ]).then(util.spread((profileDetail:IBPProfile, tcData:IProfileAttributeResponse, progress:IBPWizardProgress):void => {
        this.tc = tcData;
        this.setWizardModel(profileDetail);
        this.progress = progress;
      }));
    }

    return Promise.reject();
  }

  public setWizardModel(data?:IBPProfile):void {
    let bpProfileData:IBPProfileData;

    // setBPData is called from setBPProfileId, saveBPProfile and afterSave
    // when called from saveBPProfile, the param 'data' is not provided; as result is only setting profile attributes
    // the call in afterSave is needed to sync bpProfileData with latest bank updates when changing the bank tab
    if (data) {
      bpProfileData = {
        id: data.id,
        companyGroup: data.company?.companyGroup,
        country: data.country,
        contractCurrency: data.contractCurrency,
        taxNumber: data.taxNumber,
        vatName: data.country?.vatName || this.translationService.instant('GA.GENERAL.VATTAX'),
        businessRegistrationNr: data.businessRegistrationNr,
        companyName: data.companyCountryRegisteredName,
        orderFees: this.translationService.instant('BPWIZARD.AGREEMENT.ORDERFEES.TEXT'),
        memberPercentageOFFee: data.memberPercentageOFFee || 1,
        contractAgreed: data.contractAgreed,
        contractAgreedBy: data.contractAgreedBy,
        contractAgreedOn: data.contractAgreedOn,
        localBank: data.localBank,
        usdBank: data.usdBank,
        euroBank: data.euroBank
      };
    }

    this.model = util.copyAndExtend(bpProfileData || this.model, this.getProfileAttributeData());
    this.backupModel = util.copyAndExtend(this.model);
  }

  private getProfileAttributeData():IBPProfileAttributeData {
    return {
      defaultResellerFee: <number>this.extractProfileAttribute(ProfileAttributeCode.RESELLER_MARGIN, true),
      resellerFee: <number>this.extractProfileAttribute(ProfileAttributeCode.RESELLER_MARGIN),
      resellerFeeComment: this.extractProfileAttributeComments(ProfileAttributeCode.RESELLER_MARGIN),
      paymentTerms: <number>this.extractProfileAttribute(ProfileAttributeCode.STANDARD_PAYMENT_TERMS),
      paymentTermsComment: this.extractProfileAttributeComments(ProfileAttributeCode.STANDARD_PAYMENT_TERMS),
      invoiceCurrencyEUR: <IValueObject>this.extractProfileAttribute(ProfileAttributeCode.INVOICE_CURRENCY_EUR_FOREIGN),
      invoiceCurrencyUSD: <IValueObject>this.extractProfileAttribute(ProfileAttributeCode.INVOICE_CURRENCY_USD_FOREIGN),
      customerCCPayment: <IValueObject>this.extractProfileAttribute(ProfileAttributeCode.CUSTOMER_CC_PAYMENT),
      ccCharge: <number>this.extractProfileAttribute(ProfileAttributeCode.CC_MARGIN),
      paypalCharge: <number>this.extractProfileAttribute(ProfileAttributeCode.PAYPAL_MARGIN),
      payACSLFees: <IValueObject>this.extractProfileAttribute(ProfileAttributeCode.ACSL_FREE_TAX_LOCAL),
      withholdingTaxes: this.extractProfileAttributeComments(ProfileAttributeCode.ACSL_FREE_TAX_LOCAL)
    };
  }

  private extractProfileAttribute(code:string, getDefault?:boolean):IValueObject | number {
    const profileAttribute:IProfileAttribute = _.find(this.tc.profileAttributes, (pa:IProfileAttribute) => pa.code === code);
    if (profileAttribute?.attributeValue?.id) {
      switch (profileAttribute && profileAttribute.valueType) {
        case 'BOOLEAN':
          return <IValueObject>this.formlyService.getBooleanOptions(util.stringToBoolean(profileAttribute.attributeValue.agreeing));
        case 'REAL':
          return getDefault? profileAttribute.defaultRealValue : this.bpProfilesAttributesService.getRealAttributeValue(profileAttribute);
      }
    }
  }

  private extractProfileAttributeComments(code:string):string {
    const profileAttribute:IProfileAttribute = _.find(this.tc.profileAttributes, (pa:IProfileAttribute) => pa.code === code);
    return profileAttribute?.attributeValue?.comment;
  }

  public isCurrentStepComplete():boolean {
    return !!_.find(this.progress?.completedTabs, (completedStep:string) => completedStep === this.wizard.activeStepId());
  }

  public createBPProfileRequestData():IBPProfile {
    const possibleModifiedBPProfileFields:IBPProfile = util.copyAndExtend(this.bpProfilesService.profileDetail, this.getBPProfileRequestFields());

    // pick only defined keys
    return _.pick(possibleModifiedBPProfileFields, (value:any) => util.notNullOrUndefined(value));
  }

  private getBPProfileRequestFields():IBPProfile {
    if (!this.model) {
      return;
    }

    return {
      id: this.model.id,
      country: this.model.country,
      contractCurrency: this.model.contractCurrency,
      taxNumber: this.model.taxNumber,
      businessRegistrationNr: this.model.businessRegistrationNr,
      contractAgreed: this.model.contractAgreed,
      contractAgreedOn: this.model.contractAgreedOn,
      localBank: this.model.localBank,
      usdBank: this.model.usdBank,
      euroBank: this.model.euroBank
    };
  }

  public createProfileAttributesRequest(model:IBPProfileData):IProfileAttributeRequest[] {
    const profileAttributes:IProfileAttribute[] = util.copyAndExtend(this.tc.profileAttributes);

    _.each(profileAttributes, (profileAttribute:IProfileAttribute) => {
      const currentValue:number | IValueObject = model?.[ this.codeToKey(profileAttribute.code) ];
      if (profileAttribute.attributeValue && (currentValue || currentValue === null)) {
        if (util.isNullOrUndefinedOrEmpty(currentValue)) {
          // clear field value
          profileAttribute.attributeValue.agreeing = ProfileAttributeAgreeType.NOT_AVAILABLE;
          profileAttribute.attributeValue.realValue = undefined;
        } else if (profileAttribute.valueType === ProfileAttributeValueType.REAL) {
          // real value
          profileAttribute.attributeValue.agreeing = (currentValue !== profileAttribute.defaultRealValue)? ProfileAttributeAgreeType.NOT_AGREE : ProfileAttributeAgreeType.AGREE;
          profileAttribute.attributeValue.realValue = <number>currentValue;
        } else {
          // boolean value
          profileAttribute.attributeValue.agreeing = ((<IValueObject>currentValue).id !== profileAttribute.defaultBooleanValue)? ProfileAttributeAgreeType.NOT_AGREE : ProfileAttributeAgreeType.AGREE;
        }

        // save comments
        switch (profileAttribute.code) {
          case ProfileAttributeCode.RESELLER_MARGIN:
            profileAttribute.attributeValue.comment = model?.resellerFeeComment;
            break;
          case ProfileAttributeCode.STANDARD_PAYMENT_TERMS:
            profileAttribute.attributeValue.comment = model?.paymentTermsComment;
            break;
          case ProfileAttributeCode.ACSL_FREE_TAX_LOCAL:
            profileAttribute.attributeValue.comment = model?.withholdingTaxes;
            break;
        }
      }
    });

    return this.bpProfilesAttributesService.createProfileAttributesRequestData(profileAttributes, this.tc.profileAttributes);
  }

  private codeToKey(code:string):string {
    const mapping:IObject = {
      [ ProfileAttributeCode.RESELLER_MARGIN ]: 'resellerFee',
      [ ProfileAttributeCode.STANDARD_PAYMENT_TERMS ]: 'paymentTerms',
      [ ProfileAttributeCode.INVOICE_CURRENCY_EUR_FOREIGN ]: 'invoiceCurrencyEUR',
      [ ProfileAttributeCode.INVOICE_CURRENCY_USD_FOREIGN ]: 'invoiceCurrencyUSD',
      [ ProfileAttributeCode.CUSTOMER_CC_PAYMENT ]: 'customerCCPayment',
      [ ProfileAttributeCode.CC_MARGIN ]: 'ccCharge',
      [ ProfileAttributeCode.PAYPAL_MARGIN ]: 'paypalCharge',
      [ ProfileAttributeCode.ACSL_FREE_TAX_LOCAL ]: 'payACSLFees'
    };
    return mapping[ code ];
  }

  public clear():void {
    this.tc = undefined;
    this.progress = undefined;
    this.model = undefined;
    this.backupModel = undefined;
    this.oemRelations = undefined;
  }
}